// Genera el flyer que se adjunta al compartir: la foto del producto con el
// nombre, los precios y las características encima, para que la imagen se
// explique sola aunque se reenvíe de chat en chat sin el texto.
//
// Se dibuja en el cliente con <canvas>. La foto se normaliza antes a una ruta
// del mismo origen (el API las entrega contra www., que es otro origen y haría
// que el canvas quedara contaminado y toBlob() fallara).

import { featureLines, productImagePath } from './productShare';

const W = 1080;
const H = 1350; // 4:5, el formato que más pantalla ocupa en un chat

const BROWN = '#2c1810';
const GOLD = '#c49564';
const CREAM = '#f3e6dc';

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const PAD = 64;
const NAME_SIZE = 72;
const NAME_LINE = 84;   // interlineado del nombre
const FEATURE_LINE = 62;
const BRAND_BLOCK = 120; // el pie de marca y su respiro
const MAX_FEATURES = 5;

// Carga la imagen; si falla o tarda demasiado, cae al placeholder que ya usa el
// modal. El timeout no es teórico: sin él, una foto que nunca resuelve deja el
// botón clavado en "Preparando…" para siempre.
const IMAGE_TIMEOUT_MS = 8000;

const loadImage = (src, isFallback = false) =>
  new Promise((resolve) => {
    const img = new Image();
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (value || isFallback) return resolve(value);
      loadImage('/placeholder.jpg', true).then(resolve);
    };

    const timer = setTimeout(() => finish(null), IMAGE_TIMEOUT_MS);

    // crossOrigin SOLO cuando la foto es de otro origen. Ponerlo siempre es una
    // trampa: en una imagen del mismo sitio servida sin cabeceras CORS, el
    // navegador la rechaza y nos quedaríamos con el placeholder.
    if (/^https?:\/\//.test(src) && !src.startsWith(window.location.origin)) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => finish(img);
    img.onerror = () => finish(null);
    img.src = src;
  });

// Dibuja la imagen recortada estilo `object-fit: cover`.
const drawCover = (ctx, img, x, y, w, h) => {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
};

// Parte el texto en líneas que quepan en `maxWidth`, hasta `maxLines`.
//
// Los puntos suspensivos se ponen si y solo si REALMENTE quedó texto fuera. Que
// la última línea llegue justa al ancho no significa que falte nada: decidir el
// corte por el ancho hacía dos cosas mal a la vez —callarse texto perdido, y
// comerse caracteres buenos de una línea completa (adiós al precio de un pastel).
const wrapText = (ctx, text, maxWidth, maxLines) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let current = '';
  let dropped = 0; // palabras que no cupieron

  for (let i = 0; i < words.length; i += 1) {
    const candidate = current ? `${current} ${words[i]}` : words[i];
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
      continue;
    }
    if (lines.length + 1 >= maxLines && current) {
      // Esta era la última línea permitida: lo que queda se pierde.
      dropped = words.length - i;
      break;
    }
    if (current) lines.push(current);
    current = words[i];
  }
  if (current) lines.push(current);

  if (dropped > 0 && lines.length) {
    let last = lines[lines.length - 1];
    while (last && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1);
    lines[lines.length - 1] = `${last.replace(/[\s,;:.-]+$/, '')}…`;
  }
  return lines.length ? lines : [''];
};

// Devuelve un Blob JPEG con el flyer, o null si el navegador no puede
// exportarlo (canvas contaminado por una foto de otro origen sin CORS).
export const buildProductFlyer = async (product, type) => {
  if (typeof document === 'undefined' || !product) return null;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Sin esto el texto puede salir con la tipografía de respaldo.
  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* seguimos con la que haya */
    }
  }

  // Fondo
  ctx.fillStyle = BROWN;
  ctx.fillRect(0, 0, W, H);

  // La foto ocupa TODO el lienzo y el texto va encima. Reservarle una franja
  // fija dejaba dos defectos visibles: una costura donde la foto se cortaba, y
  // un hueco muerto abajo cuando el producto tenía pocas características.
  const img = await loadImage(productImagePath(product.image));
  if (img) drawCover(ctx, img, 0, 0, W, H);

  // Qué hay que escribir (se mide antes de decidir dónde empieza el panel).
  ctx.textBaseline = 'alphabetic';
  ctx.font = `700 ${NAME_SIZE}px ${FONT}`;
  const nameLines = wrapText(ctx, product.name, W - PAD * 2, 2);

  const features = featureLines(product, type).slice(0, 4);
  if (product.bajoPedido) features.push('Bajo pedido — 24 h de anticipación');
  if (product.available === false) features.push('Agotado por el momento');
  const shown = features.slice(0, MAX_FEATURES);

  const contentH =
    nameLines.length * NAME_LINE + shown.length * FEATURE_LINE + BRAND_BLOCK + PAD;
  const panelTop = Math.max(H * 0.42, H - contentH);

  // Velo oscuro que arranca difuminado sobre la foto: el texto se lee y la
  // imagen sigue asomando detrás, en vez de quedar cortada en seco.
  const fade = ctx.createLinearGradient(0, panelTop - 260, 0, H);
  fade.addColorStop(0, 'rgba(28,15,9,0)');
  fade.addColorStop(0.45, 'rgba(28,15,9,0.82)');
  fade.addColorStop(1, 'rgba(28,15,9,0.97)');
  ctx.fillStyle = fade;
  ctx.fillRect(0, panelTop - 260, W, H - panelTop + 260);

  // Nombre
  ctx.fillStyle = '#ffffff';
  ctx.font = `700 ${NAME_SIZE}px ${FONT}`;
  let y = panelTop + NAME_SIZE;
  for (const line of nameLines) {
    ctx.fillText(line, PAD, y);
    y += NAME_LINE;
  }

  // Características
  ctx.font = `400 40px ${FONT}`;
  ctx.fillStyle = CREAM;
  y += 14;
  for (const line of shown) {
    const [wrapped] = wrapText(ctx, line, W - PAD * 2, 1);
    ctx.fillText(wrapped, PAD, y);
    y += FEATURE_LINE;
  }

  // Pie de marca
  ctx.fillStyle = GOLD;
  ctx.font = `700 38px ${FONT}`;
  ctx.fillText('Brazolovers', PAD, H - 58);
  ctx.font = `400 34px ${FONT}`;
  ctx.textAlign = 'right';
  ctx.fillText('brazolovers.com', W - PAD, H - 58);
  ctx.textAlign = 'left';

  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    } catch {
      resolve(null); // canvas contaminado: se comparte sin foto adjunta
    }
  });
};

// Expuesto solo para las pruebas: el corte de texto es la parte con lógica
// real de este módulo y merece cubrirse sin montar un canvas.
export { wrapText as __wrapTextForTests };

export default buildProductFlyer;
